import { useField } from 'formik';
import dayjs from 'dayjs';
import { Input } from '@library/forms';
import Picker from '@library/datepicker/components/Picker';
import { Popover } from '@library/popover';
import { CalendarIcon, CrossIcon } from '@icon/index';

interface IFormikDatePicker {
    format?: string;
    minDate?: Date;
    maxDate?: Date;
    name: string;
    disabled?: boolean;
    isClearable?: boolean;
}

const FormikDatePicker = ({
    format = 'DD MMM, YYYY',
    minDate,
    maxDate,
    name,
    disabled = false,
    isClearable = true,
}: IFormikDatePicker) => {
    const [field, , helpers] = useField(name);

    const handleChange = (cusDate: dayjs.Dayjs) => {
        // Ensure valid date before setting the value
        if (!cusDate || !cusDate.isValid()) {
            helpers.setValue(''); // Clear the value if invalid
            return;
        }
        helpers.setValue(cusDate.format('YYYYMMDD')); // Set the valid date in YYYYMMDD format
    };

    // Ensure parsed value is a valid dayjs object or null
    const parsedValue = field.value ? dayjs(field.value, 'YYYYMMDD') : null;

    // Default to a valid dayjs object (empty date) if parsedValue is null
    const displayValue = parsedValue?.isValid() ? parsedValue : dayjs();

    return (
        <Popover
            placement="bottom-start"
            contentClassName="z-[82] p-0"
            content={
                <Picker
                    value={displayValue} // Always pass a valid dayjs object to Picker
                    onChange={handleChange}
                    minDate={minDate ? dayjs(minDate) : null}
                    maxDate={maxDate ? dayjs(maxDate) : null}
                />
            }
        >
            <div className="relative flex items-center">
                <Input
                    name={name}
                    readOnly
                    disabled={disabled}
                    value={parsedValue?.isValid() ? parsedValue.format(format) : ''} // Format display value
                    className="pr-10"
                />

                {/* Clear button */}
                {isClearable && parsedValue && !disabled && (
                    <div
                        className="absolute right-8 text-gray-400 hover:text-red-500 cursor-pointer"
                        onClick={() => helpers.setValue('')}
                    >
                        <CrossIcon className="w-4 h-4" />
                    </div>
                )}

                {/* Calendar Icon */}
                <div className="absolute right-3 pointer-events-none">
                    <CalendarIcon />
                </div>
            </div>
        </Popover>
    );
};

export default FormikDatePicker;
